'use client'

import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslations } from 'next-intl'
import { AlertCircle, Building, ShieldCheck, Mail, Lock } from 'lucide-react'
import { LoginSchema, type Login } from '@domownik/shared'
import { ApiError, apiClient, setToken } from '@/lib/api-client'
import { Button, Input, InteractionBlocker } from '@/components/ui'
import { cn } from '@/lib/utils'

type LoginResponse =
  | { requiresMfa: true; mfaToken: string }
  | { accessToken: string; requiresMfa?: false }

type MfaVerifyResponse = {
  accessToken: string
}

export default function LoginPage() {
  const t = useTranslations('auth')
  const [requiresMfa, setRequiresMfa] = useState(false)
  const [mfaToken, setMfaToken] = useState<string | null>(null)
  const [mfaCode, setMfaCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  // Wenn der User wegen abgelaufener Session hierher geleitet wurde,
  // zeige den Hinweis als eingebettetes Inline-Banner in der Login-Maske.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const params = new URLSearchParams(window.location.search)
    if (params.get('expired') === '1') {
      setFormError(t('sessionExpired'))
    }
    // einmalig beim Mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Login>({
    resolver: zodResolver(LoginSchema),
  })

  const getRedirectTarget = () => {
    const redirectTo = new URLSearchParams(window.location.search).get('redirect')
    return redirectTo && redirectTo.startsWith('/') && redirectTo !== '/' ? redirectTo : '/dashboard'
  }

  const unknownErrorMessage = (traceId?: string) =>
    traceId ? t('unknownErrorWithReference', { traceId }) : t('unknownError')

  const logTechnicalError = (context: string, err: unknown) => {
    if (process.env.NODE_ENV !== 'production') {
      console.error(`[${context}]`, err)
    }
  }

  const onLogin = async (data: Login) => {
    setLoading(true)
    setFormError(null)
    try {
      const res = await apiClient.post<LoginResponse>('/auth/login', data)
      if (res.requiresMfa) {
        setMfaToken(res.mfaToken)
        setRequiresMfa(true)
      } else {
        setToken(res.accessToken)
        window.location.href = getRedirectTarget()
      }
    } catch (err: unknown) {
      if (err instanceof ApiError) {
        if (err.status === 401) {
          setFormError(t('invalidCredentials'))
        } else if (err.status === 429) {
          setFormError(t('tooManyAttempts'))
        } else {
          setFormError(unknownErrorMessage(err.traceId))
          logTechnicalError('login:unknown-api-error', err)
        }
      } else if (err instanceof TypeError) {
        setFormError(t('serverUnavailable'))
      } else {
        setFormError(t('unknownError'))
        logTechnicalError('login:unknown-error', err)
      }
    } finally {
      setLoading(false)
    }
  }

  const onMfaVerify = async () => {
    if (!mfaToken) return
    setLoading(true)
    setFormError(null)
    try {
      const res = await apiClient.post<MfaVerifyResponse>('/auth/2fa/verify', {
        mfaToken,
        code: mfaCode,
      })
      setToken(res.accessToken)
      window.location.href = getRedirectTarget()
    } catch (err: unknown) {
      if (err instanceof ApiError) {
        if (err.status === 401) {
          setFormError(t('invalidCode'))
        } else if (err.status === 429) {
          setFormError(t('tooManyAttempts'))
        } else {
          setFormError(unknownErrorMessage(err.traceId))
          logTechnicalError('mfa:unknown-api-error', err)
        }
      } else if (err instanceof TypeError) {
        setFormError(t('serverUnavailable'))
      } else {
        setFormError(t('unknownError'))
        logTechnicalError('mfa:unknown-error', err)
      }
    } finally {
      setLoading(false)
    }
  }

  if (requiresMfa) {
    return (
      <AuthShell>
        <InteractionBlocker active={loading} label={t('processing')} />
        <div className="mb-6 flex flex-col items-center gap-3 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-50 ring-1 ring-primary-100">
            <ShieldCheck className="h-7 w-7 text-primary-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">{t('twoFactor')}</h1>
            <p className="mt-1 text-sm text-gray-400">{t('enterCode')}</p>
          </div>
        </div>

        <fieldset disabled={loading} aria-busy={loading} className="space-y-5">
          {formError && <FormErrorBanner message={formError} />}
          <div className="mb-5">
            <label className="mb-1.5 block text-sm font-medium text-gray-600">{t('code')}</label>
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
              className={cn(
                'w-full rounded-xl border border-gray-200 bg-white px-4 py-3.5 text-center text-2xl tracking-[0.5em] transition-all duration-200',
                'hover:border-gray-300 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200',
              )}
              placeholder={t('codePlaceholder')}
              autoFocus
            />
          </div>
          <Button
            variant="primary"
            size="lg"
            className="w-full"
            loading={loading}
            disabled={loading || mfaCode.length !== 6}
            onClick={onMfaVerify}
          >
            {t('verifyButton')}
          </Button>
        </fieldset>
      </AuthShell>
    )
  }

  return (
    <AuthShell>
      <InteractionBlocker active={loading} label={t('processing')} />
      <div className="mb-8 flex flex-col items-center gap-4 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-primary-500 to-primary-600 shadow-lg shadow-primary-200">
          <Building className="h-7 w-7 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Domownik</h1>
          <p className="mt-0.5 text-sm text-gray-400">Hausverwaltung</p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onLogin)} className="space-y-5" aria-busy={loading}>
        <fieldset disabled={loading} className="space-y-5">
          {formError && <FormErrorBanner message={formError} />}
          <Input
            label={t('email')}
            type="email"
            leadingIcon={Mail}
            error={errors.email?.message}
            placeholder={t('emailPlaceholder')}
            autoComplete="email"
            {...register('email')}
          />
          <Input
            label={t('password')}
            type="password"
            leadingIcon={Lock}
            error={errors.password?.message}
            autoComplete="current-password"
            {...register('password')}
          />
          <Button
            type="submit"
            variant="primary"
            size="lg"
            className="mt-2 w-full"
            loading={loading}
          >
            {t('loginButton')}
          </Button>
        </fieldset>
      </form>
    </AuthShell>
  )
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function FormErrorBanner({ message }: { message: string }) {
  return (
    <div
      role="alert"
      aria-live="assertive"
      className="flex items-start gap-2.5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
    >
      <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-500" aria-hidden="true" />
      <span className="leading-relaxed">{message}</span>
    </div>
  )
}

function AuthShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative flex min-h-screen items-center justify-center bg-gradient-to-b from-surface-100 via-surface-50 to-white px-4 py-10">
      {/* Dekorativer Hintergrund */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -right-28 -top-28 h-[28rem] w-[28rem] rounded-full bg-primary-300/30 blur-3xl" />
        <div className="absolute -bottom-36 -left-36 h-[30rem] w-[30rem] rounded-full bg-primary-200/35 blur-3xl" />
        <div className="absolute left-1/2 top-1/3 h-72 w-72 -translate-x-1/2 rounded-full bg-primary-100/40 blur-3xl" />
        <div
          className="absolute inset-0 opacity-[0.16]"
          style={{
            backgroundImage:
              'radial-gradient(circle at 1px 1px, rgb(13 148 136 / 0.22) 1px, transparent 0)',
            backgroundSize: '22px 22px',
          }}
        />
      </div>

      <div className="relative w-full max-w-md rounded-3xl border border-primary-100/70 bg-white/95 p-8 shadow-card backdrop-blur-sm">
        {children}
      </div>
    </div>
  )
}
